<template>
  <van-popup
    v-model="visible"
    position="bottom"
    round
    :style="{ height: '75%', display: 'flex', flexDirection: 'column' }"
    @close="onClose"
  >
    <div class="address-selector-container">
      <!-- 顶部标题与关闭按钮 -->
      <div class="selector-header">
        <span class="header-title">选择</span>
        <div class="header-close-btn" @click="onClose">✕</div>
      </div>

      <!-- 面包屑层级导航 -->
      <div class="breadcrumbs-container">
        <!-- 第一级：省/州 -->
        <div
          class="breadcrumb-item"
          :class="{ 'breadcrumb-active': currentLevel === 0 }"
          @click="tapBreadcrumb(0)"
        >
          {{
            selectedProvince
              ? selectedProvince.areaName
              : countryCode === "CN"
              ? "省"
              : "州/省"
          }}
        </div>

        <!-- 分隔符 -->
        <span
          class="breadcrumb-arrow"
          v-if="selectedProvince || currentLevel >= 1"
          >❯</span
        >

        <!-- 第二级：城市 -->
        <div
          class="breadcrumb-item"
          :class="{ 'breadcrumb-active': currentLevel === 1 }"
          @click="tapBreadcrumb(1)"
          v-if="selectedProvince || currentLevel >= 1"
        >
          {{ selectedCity ? selectedCity.areaName : "城市" }}
        </div>

        <!-- 分隔符 -->
        <span class="breadcrumb-arrow" v-if="selectedCity || currentLevel >= 2"
          >❯</span
        >

        <!-- 第三级：区/邮政编码 -->
        <div
          class="breadcrumb-item"
          :class="{ 'breadcrumb-active': currentLevel === 2 }"
          @click="tapBreadcrumb(2)"
          v-if="selectedCity || currentLevel >= 2"
        >
          {{
            selectedDistrict
              ? selectedDistrict.areaName
              : countryCode === "CN"
              ? "区"
              : "邮政编码"
          }}
        </div>
      </div>

      <!-- 搜索栏 -->
      <div class="search-bar-container">
        <div class="search-input-box">
          <van-icon
            name="search"
            size="16px"
            color="#aaaaaa"
            class="search-icon"
          />
          <input
            class="search-input"
            v-model="keyword"
            :placeholder="
              '搜索' +
                (currentLevel === 0
                  ? countryCode === 'CN'
                    ? '省'
                    : '州/省'
                  : currentLevel === 1
                  ? '城市'
                  : countryCode === 'CN'
                  ? '区'
                  : '邮政编码')
            "
            @input="onSearchInput"
          />
          <van-icon
            v-if="keyword"
            name="clear"
            size="16px"
            color="#cccccc"
            class="clear-icon"
            @click="clearSearch"
          />
        </div>
      </div>

      <!-- 主体列表与字母检索区 -->
      <div class="list-wrapper">
        <!-- 滚动列表 -->
        <div class="scroll-list" ref="scrollList" @scroll="onScroll">
          <!-- 占位锚点 -->
          <div
            id="top-anchor"
            style="height: 1px; width: 100%; background: transparent;"
          ></div>

          <div class="empty-placeholder" v-if="sections.length === 0">
            <span>未检索到相关区域</span>
          </div>

          <div v-for="section in sections" :key="section.tag">
            <!-- 字母分组Header -->
            <div class="section-header-tag" :id="'group-' + section.tag">
              {{ section.tag }}
            </div>

            <!-- 分组下的列表项 -->
            <div
              class="area-list-item"
              :class="{ 'item-selected': isItemSelected(item) }"
              v-for="item in section.items"
              :key="item.areaCode || item.id"
              @click="selectItem(item)"
            >
              <span class="item-name">{{ item.areaName }}</span>
              <van-icon
                name="success"
                size="16px"
                color="#DD9132"
                v-if="isItemSelected(item)"
              />
            </div>
          </div>

          <!-- 触底增量加载提示 -->
          <div
            class="more-data-tip"
            v-if="originList.length > visibleCount && sections.length > 0"
          >
            <span>正在加载更多区域...</span>
          </div>
        </div>

        <!-- 右侧字母快捷索引 IndexBar -->
        <div class="index-bar" v-if="tags.length > 1">
          <div
            class="index-bar-letter"
            v-for="tag in tags"
            :key="tag"
            @click="jumpToTag(tag)"
          >
            {{ tag }}
          </div>
        </div>
      </div>
    </div>
  </van-popup>
</template>

<script>
export default {
  name: "SelectionStyle5AddressSheet",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    countryCode: {
      type: String,
      default: "CN"
    },
    province: {
      type: String,
      default: ""
    },
    city: {
      type: String,
      default: ""
    },
    district: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      visible: this.show,
      currentLevel: 0, // 0: 省/州, 1: 城市, 2: 区/邮编
      selectedProvince: null,
      selectedCity: null,
      selectedDistrict: null,

      keyword: "", // 搜索关键字
      originList: [], // 当前层级的原始列表
      sections: [], // 按字母分组后的数据 [{tag: 'A', items: [...]}]
      tags: [], // 当前存在的分组字母索引
      visibleCount: 100 // 增量懒渲染最大行数
    };
  },
  watch: {
    show(newVal) {
      this.visible = newVal;
      if (newVal) {
        this.getAddressData();
      }
    },
    visible(newVal) {
      if (!newVal) {
        this.$emit("close");
      }
    },
    countryCode() {
      if (this.visible) {
        this.initSelector();
      }
    }
  },
  created() {
    // 原始数据挂在普通实例属性上，避免海量嵌套对象进入 Vue 的响应式依赖收集，防止卡顿
    this.allAddressData = [];
    this.countryProvList = [];
    if (this.show) {
      this.getAddressData();
    }
  },
  methods: {
    onClose() {
      this.visible = false;
    },
    getAddressData() {
      this.$store
        .dispatch("mine/getAllAddress")
        .then(() => {
          const addressData = this.$store.getters.addressData || [];
          this.allAddressData = addressData;
          this.initSelector();
        })
        .catch(() => {});
    },
    initSelector() {
      let showList = this.allAddressData || [];

      // 1. 根据国家代码过滤出顶级省份/州列表（部分环境顶级为国家CN/AU节点）
      const hasCountryGroup = showList.some(
        e =>
          e.id === "CN" ||
          e.id === "AU" ||
          e.areaCode === "CN" ||
          e.areaCode === "AU"
      );
      if (hasCountryGroup) {
        const countryNode = showList.find(
          e => e.id === this.countryCode || e.areaCode === this.countryCode
        );
        showList = countryNode ? countryNode.childList || [] : [];
      }

      this.countryProvList = showList;

      let selectedProvince = null;
      let selectedCity = null;
      let selectedDistrict = null;
      let currentLevel = 0;

      // 2. 级联定位回显指针
      if (this.province) {
        const prov = this.countryProvList.find(
          e => e.areaName === this.province
        );
        if (prov) {
          selectedProvince = prov;
          currentLevel = 1;
          showList = prov.childList || [];

          if (this.city) {
            const ct = showList.find(e => e.areaName === this.city);
            if (ct) {
              selectedCity = ct;
              currentLevel = 2;
              showList = ct.childList || [];

              if (this.district) {
                const dist = showList.find(e => e.areaName === this.district);
                if (dist) {
                  selectedDistrict = dist;
                  currentLevel = 2; // 定位在最后一层，高亮选中项
                }
              }
            }
          }
        }
      }

      this.selectedProvince = selectedProvince;
      this.selectedCity = selectedCity;
      this.selectedDistrict = selectedDistrict;
      this.currentLevel = currentLevel;
      this.keyword = "";
      this.visibleCount = 100;

      this.setLevelData(currentLevel, showList);

      this.$nextTick(() => {
        if (this.$refs.scrollList) {
          this.$refs.scrollList.scrollTop = 0;
        }
      });
    },
    setLevelData(level, dataList) {
      // 惰性首字母预计算缓存
      dataList.forEach(item => {
        if (!item._tag) {
          item._tag = this.getFirstLetter(item.areaName);
        }
      });

      // 1. 过滤搜索关键字
      const keyword = (this.keyword || "").trim().toLowerCase();
      let filtered = dataList || [];
      if (keyword) {
        filtered = filtered.filter(
          item => (item.areaName || "").toLowerCase().indexOf(keyword) > -1
        );
      }

      // 2. 字母索引提取
      const fullGrouped = {};
      filtered.forEach(item => {
        const tag = item._tag;
        fullGrouped[tag] = true;
      });
      const sortedTags = Object.keys(fullGrouped).sort();

      // 3. 增量渲染防卡顿
      const rawLength = filtered.length;
      let currentVisible = this.visibleCount;
      if (keyword && rawLength < 150) {
        currentVisible = rawLength;
      }
      const slicedFiltered = filtered.slice(0, currentVisible);

      // 4. 按字母分组构建 sections
      const grouped = {};
      slicedFiltered.forEach(item => {
        const tag = item._tag;
        if (!grouped[tag]) {
          grouped[tag] = [];
        }
        grouped[tag].push(item);
      });

      const contentTags = Object.keys(grouped).sort();
      const sections = contentTags.map(tag => {
        const items = grouped[tag].sort((a, b) =>
          a.areaName.localeCompare(b.areaName, "zh")
        );
        return {
          tag: tag,
          items: items
        };
      });

      this.currentLevel = level;
      this.originList = dataList;
      this.sections = sections;
      this.tags = sortedTags;
    },
    getFirstLetter(str) {
      if (!str || typeof str !== "string") return "#";
      const trimmed = str.trim();
      if (trimmed.length === 0) return "#";

      const firstChar = trimmed.charAt(0);
      if (/[a-zA-Z]/.test(firstChar)) {
        return firstChar.toUpperCase();
      }

      const charList = [
        { letter: "A", char: "啊" },
        { letter: "B", char: "芭" },
        { letter: "C", char: "擦" },
        { letter: "D", char: "搭" },
        { letter: "E", char: "蛾" },
        { letter: "F", char: "发" },
        { letter: "G", char: "噶" },
        { letter: "H", char: "哈" },
        { letter: "J", char: "击" },
        { letter: "K", char: "喀" },
        { letter: "L", char: "垃" },
        { letter: "M", char: "妈" },
        { letter: "N", char: "拿" },
        { letter: "O", char: "哦" },
        { letter: "P", char: "啪" },
        { letter: "Q", char: "期" },
        { letter: "R", char: "然" },
        { letter: "S", char: "撒" },
        { letter: "T", char: "塌" },
        { letter: "W", char: "挖" },
        { letter: "X", char: "昔" },
        { letter: "Y", char: "压" },
        { letter: "Z", char: "匝" }
      ];
      for (let i = charList.length - 1; i >= 0; i--) {
        if (firstChar.localeCompare(charList[i].char, "zh") >= 0) {
          return charList[i].letter;
        }
      }
      return "#";
    },
    onSearchInput() {
      this.visibleCount = 100;
      this.setLevelData(this.currentLevel, this.originList);
    },
    clearSearch() {
      this.keyword = "";
      this.onSearchInput();
    },
    jumpToTag(tag) {
      this.visibleCount = this.originList.length;
      this.setLevelData(this.currentLevel, this.originList);

      this.$nextTick(() => {
        const element = this.$el.querySelector(`#group-${tag}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    onScroll(e) {
      const container = e.target;
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 50
      ) {
        this.onScrollToLower();
      }
    },
    onScrollToLower() {
      if (this.visibleCount >= this.originList.length) return;
      this.visibleCount = Math.min(
        this.visibleCount + 100,
        this.originList.length
      );
      this.setLevelData(this.currentLevel, this.originList);
    },
    isItemSelected(item) {
      if (this.currentLevel === 0) {
        if (!this.selectedProvince) return false;
        if (this.selectedProvince.areaCode && item.areaCode) {
          return this.selectedProvince.areaCode === item.areaCode;
        }
        if (this.selectedProvince.id && item.id) {
          return this.selectedProvince.id === item.id;
        }
        return this.selectedProvince.areaName === item.areaName;
      } else if (this.currentLevel === 1) {
        if (!this.selectedCity) return false;
        if (this.selectedCity.areaCode && item.areaCode) {
          return this.selectedCity.areaCode === item.areaCode;
        }
        if (this.selectedCity.id && item.id) {
          return this.selectedCity.id === item.id;
        }
        return this.selectedCity.areaName === item.areaName;
      } else if (this.currentLevel === 2) {
        if (!this.selectedDistrict) return false;
        if (this.selectedDistrict.areaCode && item.areaCode) {
          return this.selectedDistrict.areaCode === item.areaCode;
        }
        if (this.selectedDistrict.id && item.id) {
          return this.selectedDistrict.id === item.id;
        }
        return this.selectedDistrict.areaName === item.areaName;
      }
      return false;
    },
    selectItem(item) {
      this.visibleCount = 100;

      if (this.currentLevel === 0) {
        this.selectedProvince = item;
        this.selectedCity = null;
        this.selectedDistrict = null;
        this.keyword = "";

        const children = item.childList || [];
        if (children.length === 0) {
          this.completeSelection();
        } else {
          this.setLevelData(1, children);
        }
      } else if (this.currentLevel === 1) {
        this.selectedCity = item;
        this.selectedDistrict = null;
        this.keyword = "";

        const children = item.childList || [];
        if (children.length === 0) {
          this.completeSelection();
        } else {
          this.setLevelData(2, children);
        }
      } else {
        this.selectedDistrict = item;
        this.completeSelection();
      }

      this.$nextTick(() => {
        if (this.$refs.scrollList) {
          this.$refs.scrollList.scrollTop = 0;
        }
      });
    },
    tapBreadcrumb(level) {
      this.keyword = "";
      this.visibleCount = 100;

      if (level === 0) {
        this.selectedProvince = null;
        this.selectedCity = null;
        this.selectedDistrict = null;
        this.setLevelData(0, this.countryProvList);
      } else if (level === 1) {
        this.selectedCity = null;
        this.selectedDistrict = null;
        this.setLevelData(
          1,
          this.selectedProvince ? this.selectedProvince.childList || [] : []
        );
      } else if (level === 2) {
        this.selectedDistrict = null;
        this.setLevelData(
          2,
          this.selectedCity ? this.selectedCity.childList || [] : []
        );
      }

      this.$nextTick(() => {
        if (this.$refs.scrollList) {
          this.$refs.scrollList.scrollTop = 0;
        }
      });
    },
    completeSelection() {
      const result = [
        {
          areaName: this.selectedProvince ? this.selectedProvince.areaName : ""
        },
        { areaName: this.selectedCity ? this.selectedCity.areaName : "" },
        {
          areaName: this.selectedDistrict ? this.selectedDistrict.areaName : ""
        }
      ];

      this.$emit("getSelectAddress", result);
      this.$emit("close");
    }
  }
};
</script>

<style lang="less" scoped>
.address-selector-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: #ffffff;
  box-sizing: border-box;
  overflow: hidden;
}

/* 顶部标题与关闭按钮 */
.selector-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 55px;
  border-bottom: 1px solid #eeeeee;
  box-sizing: border-box;
  flex-shrink: 0;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #333333;
}

.header-close-btn {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #666666;
  padding: 5px;
  transition: opacity 0.2s ease;
  cursor: pointer;
}

.header-close-btn:active {
  opacity: 0.5;
}

/* 面包屑导航 */
.breadcrumbs-container {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  box-sizing: border-box;
  overflow-x: auto;
  white-space: nowrap;
  border-bottom: 1px solid #fafafa;
  flex-shrink: 0;
}

.breadcrumb-item {
  font-size: 14px;
  color: #999999;
  font-weight: 400;
  transition: all 0.2s ease;
  cursor: pointer;
  padding: 2px 0;
}

.breadcrumb-active {
  color: #333333;
  font-weight: 600;
  border-bottom: 2px solid #dd9132;
}

.breadcrumb-arrow {
  font-size: 12px;
  color: #cccccc;
  margin: 0 8px;
}

/* 搜索栏 */
.search-bar-container {
  padding: 8px 15px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.search-input-box {
  display: flex;
  align-items: center;
  height: 38px;
  background-color: #f3f4f5;
  border-radius: 6px;
  padding: 0 12px;
  box-sizing: border-box;
}

.search-icon {
  margin-right: 8px;
}

.clear-icon {
  margin-left: 8px;
  cursor: pointer;
}

.search-input {
  flex: 1;
  height: 100%;
  font-size: 14px;
  color: #333333;
  background: transparent;
  border: none;
  outline: none;
  &::placeholder {
    color: #aaaaaa;
  }
}

/* 主体列表区域 */
.list-wrapper {
  position: relative;
  flex: 1;
  display: flex;
  overflow: hidden;
}

.scroll-list {
  flex: 1;
  height: 100%;
  overflow-y: auto;
  padding-right: 30px; /* 为右侧 IndexBar 留出空间 */
  box-sizing: border-box;
}

.empty-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  font-size: 14px;
  color: #999999;
}

/* 字母标签 Header */
.section-header-tag {
  display: flex;
  align-items: center;
  height: 32px;
  background-color: #f3f4f5;
  padding-left: 15px;
  font-size: 13px;
  font-weight: 600;
  color: #666666;
  box-sizing: border-box;
}

/* 地区列表条目 */
.area-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 49px;
  padding: 0 24px 0 15px;
  border-bottom: 1px solid #eeeeee;
  box-sizing: border-box;
  background-color: #ffffff;
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.area-list-item:active {
  background-color: #f9f9f9;
}

.item-name {
  font-size: 14px;
  color: #333333;
}

.item-selected {
  background-color: #fafafa;
}

.item-selected .item-name {
  font-weight: 600;
  color: #dd9132;
}

/* 右侧字母快速检索 IndexBar */
.index-bar {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 5px 2px;
  border-radius: 10px;
  z-index: 10;
}

.index-bar-letter {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 16px;
  font-size: 10px;
  font-weight: 600;
  color: #333333;
  transition: transform 0.2s ease;
  cursor: pointer;
}

.index-bar-letter:active {
  transform: scale(1.3);
  color: #dd9132;
}

/* 列表底部懒加载提示 */
.more-data-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px 15px 30px 15px;
  font-size: 12px;
  color: #999999;
  text-align: center;
  background-color: #fafafa;
}
</style>
